/**
 * KnowledgeModal — znalosti projektu (Lovable parity "Knowledge").
 *
 * Tab „Instrukce": volný text (brand, konvence) přes codable.knowledge,
 * asistent ho dostává v každé zprávě. Limit 8000 znaků (backend validace).
 *
 * Tab „Zdroje (repo)": MD báze projektu — `docs/**\/*.md` + `README.md`
 * verzované v repu. Náhled přes codable.files, editace/vytvoření přes
 * codable.chat_file_write (commit do GitLabu). Asistent je čte tool_read.
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileText, Info, Pencil, Plus } from 'lucide-react';
import { api } from '../../../lib/api';
import {
  BackButton,
  Button,
  EmptyState,
  Input,
  Label,
  Modal,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
} from '../../../components/ui';

const MAX_LEN = 8000;

interface GetResp {
  ok: boolean;
  knowledge?: string | null;
  error?: string;
}

interface SetResp {
  ok: boolean;
  length?: number;
  error?: string;
}

interface TreeEntry {
  path: string;
  name?: string;
  type: 'blob' | 'tree';
}

interface TreeResp {
  ok: boolean;
  entries?: TreeEntry[];
  error?: string;
}

interface ReadResp {
  ok: boolean;
  content?: string;
  error?: string;
}

interface WriteResp {
  ok: boolean;
  error?: string;
}

/** Vynutí konvenci zdrojů: prefix docs/ + přípona .md. */
function normalizeDocPath(raw: string): string {
  let p = raw.trim().replace(/^\/+/, '');
  if (!p) return '';
  if (!p.endsWith('.md')) p += '.md';
  if (!p.startsWith('docs/')) p = `docs/${p}`;
  return p;
}

export default function KnowledgeModal(
  { slug, onClose, onBack, initialTab = 'instructions' }: {
    slug: string;
    onClose: () => void;
    onBack?: () => void;
    initialTab?: 'instructions' | 'sources';
  },
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(initialTab);

  // ── Tab Instrukce (codable.knowledge) ────────────────────────────────────
  const [content, setContent] = useState('');
  const [loaded, setLoaded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['codable-knowledge', slug],
    queryFn: () => api.mcp<GetResp>('codable.knowledge', { slug, action: 'get' }),
  });

  // Naplnit textarea po prvním načtení — bez přepisování rozeditovaného obsahu.
  useEffect(() => {
    if (!loaded && data?.ok) {
      setContent(data.knowledge ?? '');
      setLoaded(true);
    }
  }, [data, loaded]);

  const save = useMutation({
    mutationFn: () => api.mcp<SetResp>('codable.knowledge', { slug, action: 'set', content }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.knowledgeSaveError'));
        return;
      }
      toast.success(t('codable.knowledgeSaved'));
      queryClient.invalidateQueries({ queryKey: ['codable-project'] });
      (onBack ?? onClose)();
    },
    onError: (e: unknown) =>
      toast.error(t('codable.errorGeneric', { msg: String((e as Error).message ?? e) })),
  });

  const overLimit = content.length > MAX_LEN;

  // ── Tab Zdroje (repo: docs/**/*.md + README.md) ──────────────────────────
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorIsNew, setEditorIsNew] = useState(false);
  const [editorPath, setEditorPath] = useState('');
  const [editorContent, setEditorContent] = useState('');

  const tree = useQuery({
    queryKey: ['codable-tree', slug],
    queryFn: () => api.mcp<TreeResp>('codable.files', { slug, action: 'tree' }),
    staleTime: 60_000,
  });

  const docFiles = (tree.data?.entries ?? []).filter(
    (e) => e.type === 'blob' && (e.path === 'README.md' || /^docs\/.+\.md$/.test(e.path)),
  );

  const file = useQuery({
    queryKey: ['codable-file', slug, selectedPath],
    queryFn: () => api.mcp<ReadResp>('codable.files', { slug, action: 'read', path: selectedPath }),
    enabled: !!selectedPath,
    staleTime: 60_000,
  });

  const writeFile = useMutation({
    mutationFn: (vars: { path: string; content: string }) =>
      api.mcp<WriteResp>('codable.chat_file_write', {
        slug,
        path: vars.path,
        content: vars.content,
        commit_message: `docs: ${vars.path} (Knowledge)`,
      }),
    onSuccess: (r, vars) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.knowledgeFileSaveError'));
        return;
      }
      toast.success(t('codable.knowledgeFileSaved', { path: vars.path }));
      queryClient.invalidateQueries({ queryKey: ['codable-tree', slug] });
      queryClient.invalidateQueries({ queryKey: ['codable-file', slug, vars.path] });
      setEditorOpen(false);
      setSelectedPath(vars.path);
    },
    onError: (e: unknown) =>
      toast.error(t('codable.errorGeneric', { msg: String((e as Error).message ?? e) })),
  });

  function startNewFile() {
    setEditorIsNew(true);
    setEditorPath('');
    setEditorContent('');
    setEditorOpen(true);
  }

  function startEdit() {
    if (!selectedPath) return;
    setEditorIsNew(false);
    setEditorPath(selectedPath);
    setEditorContent(file.data?.content ?? '');
    setEditorOpen(true);
  }

  function submitEditor() {
    const path = editorIsNew ? normalizeDocPath(editorPath) : editorPath;
    if (!path) {
      toast.error(t('codable.knowledgePathRequired'));
      return;
    }
    writeFile.mutate({ path, content: editorContent });
  }

  return (
    <Modal
      open
      onClose={onClose}
      onBack={onBack}
      backLabel={t('codable.wsSettings')}
      title={t('codable.knowledgeTitle')}
      size='lg'
      footer={activeTab === 'instructions'
        ? (
          <>
            <span
              className={`text-xs mr-auto ${
                overLimit ? 'text-red-400 font-medium' : 'text-gray-600'
              }`}
            >
              {content.length}/{MAX_LEN}
            </span>
            <Button variant='secondary' size='md' onClick={onClose}>
              {t('codable.knowledgeClose')}
            </Button>
            <Button
              variant='primary'
              size='md'
              loading={save.isPending}
              disabled={overLimit || isLoading}
              onClick={() => save.mutate()}
            >
              {t('codable.knowledgeSave')}
            </Button>
          </>
        )
        : (
          <Button variant='secondary' size='md' onClick={onClose}>
            {t('codable.knowledgeClose')}
          </Button>
        )}
    >
      <Tabs defaultTab={initialTab} onTabChange={(id) => setActiveTab(id as 'instructions' | 'sources')}>
        <TabList>
          <Tab id='instructions'>{t('codable.knowledgeTabInstructions')}</Tab>
          <Tab id='sources' icon={<FileText size={13} />}>{t('codable.knowledgeTabSources')}</Tab>
        </TabList>

        <TabPanel id='instructions'>
          <div className='space-y-3'>
            <p className='text-sm text-gray-400'>
              {t('codable.knowledgeHint')}
            </p>
            {data?.ok === false && (
              <div className='text-xs text-red-400'>{data.error ?? t('codable.knowledgeLoadError')}</div>
            )}
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className='min-h-64 font-mono text-xs'
              placeholder={isLoading
                ? t('codable.knowledgeLoading')
                : t('codable.knowledgePh')}
              disabled={isLoading}
              spellCheck={false}
            />
          </div>
        </TabPanel>

        <TabPanel id='sources'>
          <div className='space-y-3'>
            <div className='flex items-start justify-between gap-3'>
              <p className='text-xs text-gray-500 flex items-center gap-1.5 pt-1.5'>
                <Info size={13} className='flex-shrink-0 text-gray-600' />
                {t('codable.knowledgeSourcesHint')}
              </p>
              <Button
                variant='secondary'
                size='sm'
                icon={<Plus size={13} />}
                onClick={startNewFile}
                disabled={editorOpen && editorIsNew}
              >
                {t('codable.knowledgeNewFile')}
              </Button>
            </div>

            {tree.isLoading && (
              <div className='text-xs text-gray-500'>{t('codable.knowledgeTreeLoading')}</div>
            )}
            {tree.data?.ok === false && (
              <div className='text-xs text-red-400'>
                {tree.data.error ?? t('codable.knowledgeTreeError')}
              </div>
            )}

            {editorOpen
              ? (
                <div className='space-y-3 border border-gray-800 rounded-xl p-3 bg-gray-950/50'>
                  <BackButton
                    onClick={() => setEditorOpen(false)}
                    label={t('codable.knowledgeTabSources')}
                  />
                  <div>
                    <Label>{t('codable.knowledgeRepoPath')}</Label>
                    <Input
                      size='sm'
                      className='font-mono'
                      value={editorPath}
                      onChange={(e) => setEditorPath(e.target.value)}
                      onBlur={editorIsNew
                        ? () => setEditorPath(normalizeDocPath(editorPath))
                        : undefined}
                      placeholder='docs/nazev.md'
                      disabled={!editorIsNew}
                      spellCheck={false}
                    />
                    {editorIsNew && (
                      <p className='text-[10px] text-gray-600 mt-1'>
                        {t('codable.knowledgeMdAuto')}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>{t('codable.knowledgeContentMd')}</Label>
                    <Textarea
                      value={editorContent}
                      onChange={(e) => setEditorContent(e.target.value)}
                      className='min-h-64 font-mono text-xs'
                      placeholder='# …'
                      spellCheck={false}
                    />
                  </div>
                  <div className='flex justify-end gap-2'>
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() => setEditorOpen(false)}
                      disabled={writeFile.isPending}
                    >
                      {t('codable.cancel')}
                    </Button>
                    <Button
                      variant='primary'
                      size='sm'
                      loading={writeFile.isPending}
                      onClick={submitEditor}
                    >
                      {t('codable.save')}
                    </Button>
                  </div>
                </div>
              )
              : docFiles.length === 0 && !tree.isLoading
              ? (
                <EmptyState
                  icon={<FileText size={24} />}
                  title={t('codable.knowledgeNoSources')}
                  description={t('codable.knowledgeCreateFirst')}
                />
              )
              : (
                <>
                  <div className='space-y-0.5 max-h-40 overflow-auto'>
                    {docFiles.map((f) => (
                      <button
                        key={f.path}
                        onClick={() => setSelectedPath(f.path)}
                        className={`w-full flex items-center gap-1.5 px-2 py-1 rounded-lg text-left text-xs transition-colors ${
                          selectedPath === f.path
                            ? 'bg-blue-500/10 text-blue-300'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
                        }`}
                        title={f.path}
                      >
                        <FileText size={13} className='flex-shrink-0' />
                        <span className='font-mono truncate'>{f.path}</span>
                      </button>
                    ))}
                  </div>

                  {selectedPath && (
                    <div className='space-y-1.5'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-xs font-mono text-gray-500 truncate'>
                          {selectedPath}
                        </span>
                        <Button
                          variant='ghost'
                          size='sm'
                          icon={<Pencil size={13} />}
                          onClick={startEdit}
                          disabled={file.isLoading}
                        >
                          {t('codable.knowledgeEdit')}
                        </Button>
                      </div>
                      {file.isLoading
                        ? <div className='text-xs text-gray-500'>{t('codable.knowledgeLoading')}</div>
                        : file.data?.ok === false
                        ? (
                          <div className='text-xs text-red-400'>
                            {file.data.error ?? t('codable.knowledgeFileError')}
                          </div>
                        )
                        : (
                          <pre className='text-xs font-mono text-gray-300 bg-gray-950 border border-gray-800 rounded-xl p-3 max-h-64 overflow-auto whitespace-pre-wrap'>
                            {file.data?.content ?? ''}
                          </pre>
                        )}
                    </div>
                  )}
                </>
              )}
          </div>
        </TabPanel>
      </Tabs>
    </Modal>
  );
}
